/**
 * Locale-aware date rendering for backend ISO timestamps. Pass the active
 * i18n language so EN/UK render their native formats.
 */
export function formatDate(iso: string, locale?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(date);
}

/** Time-of-day for calendar/schedule entries (locale-aware, no seconds). */
export function formatTime(iso: string, locale?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(date);
}

/**
 * Display form of a DRF decimal string (points, scores, max scores): drops the
 * trailing `.00` noise while leaving real fractions alone.
 */
export function formatDecimal(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '0';
  const numeric = Number(value);
  return Number.isNaN(numeric) ? value : String(numeric);
}

/** Date + time together — due dates, availability windows, submission stamps. */
export function formatDateTime(iso: string, locale?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

/**
 * Backend ISO timestamp → the `YYYY-MM-DDTHH:mm` a `datetime-local` input
 * expects, in the viewer's local time (empty string for null/invalid).
 */
export function toDateTimeLocalValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/** `datetime-local` value → an ISO timestamp for the API (null when empty). */
export function fromDateTimeLocalValue(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** Human file size for asset chips (backend sends bytes). Locale-aware digits;
 *  the unit set is intentionally tiny — lesson uploads cap at tens of MB. */
export function formatBytes(bytes: number, locale?: string): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  const units = ['byte', 'kilobyte', 'megabyte', 'gigabyte'] as const;
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return new Intl.NumberFormat(locale, {
    style: 'unit',
    unit: units[unit],
    maximumFractionDigits: value >= 10 || unit === 0 ? 0 : 1,
  }).format(value);
}

const RELATIVE_UNITS: readonly [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 60 * 60],
  ['month', 30 * 24 * 60 * 60],
  ['week', 7 * 24 * 60 * 60],
  ['day', 24 * 60 * 60],
  ['hour', 60 * 60],
  ['minute', 60],
];

/**
 * "3 minutes ago" for chat previews and feed timestamps — `Intl` only, no date
 * library (FE-ADR-000 keeps the dependency tail short). Anything under a minute
 * reads as "now"; the exact stamp stays available as a `title`/tooltip.
 */
export function formatRelativeTime(iso: string, locale?: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const seconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const magnitude = Math.abs(seconds);
  for (const [unit, size] of RELATIVE_UNITS) {
    if (magnitude >= size) return formatter.format(Math.round(seconds / size), unit);
  }
  return formatter.format(0, 'second');
}
